import { getUserEntitlements } from "../config/plans";

function getMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getDayKey(now = Date.now()) {
  return new Date(now).toISOString().slice(0, 10);
}

function hashString(input = "") {
  let hash = 0;

  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 31 + input.charCodeAt(index)) >>> 0;
  }

  return hash;
}

function rotateList(values = [], rotationSeed = "") {
  if (values.length <= 1) return values;

  const offset = hashString(rotationSeed) % values.length;
  return [...values.slice(offset), ...values.slice(0, offset)];
}

export function normalizeEditorMainGame(mainGame) {
  if (!mainGame) return "";
  if (typeof mainGame === "object") {
    return String(mainGame.categoryName || mainGame.name || "").trim();
  }

  return String(mainGame).trim();
}

export function isEditorSpotlightActive(editor, now = Date.now()) {
  const entitlements = getUserEntitlements(editor);
  const spotlightUntil = getMillis(
    editor?.visibilityBoosts?.homepageSpotlightUntil
  );

  return Boolean(entitlements.homepageSpotlight || spotlightUntil > now);
}

export function isEditorFeaturedActive(editor, now = Date.now()) {
  const entitlements = getUserEntitlements(editor);
  const featuredUntil = getMillis(
    editor?.visibilityBoosts?.featuredSpotlightUntil
  );

  return Boolean(entitlements.featuredPlacement || featuredUntil > now);
}

function sortByStableFeaturedScore(editors = [], now = Date.now()) {
  return [...editors].sort((left, right) => {
    const leftEntitlements = getUserEntitlements(left);
    const rightEntitlements = getUserEntitlements(right);
    const leftBoost = getMillis(left?.visibilityBoosts?.homepageSpotlightUntil);
    const rightBoost = getMillis(right?.visibilityBoosts?.homepageSpotlightUntil);
    const leftPermanentSpotlight = Boolean(leftEntitlements.homepageSpotlight);
    const rightPermanentSpotlight = Boolean(rightEntitlements.homepageSpotlight);
    const leftFeaturedBoost = getMillis(left?.visibilityBoosts?.featuredSpotlightUntil);
    const rightFeaturedBoost = getMillis(right?.visibilityBoosts?.featuredSpotlightUntil);
    const leftViews = Number(left?.analytics?.profileViews || 0);
    const rightViews = Number(right?.analytics?.profileViews || 0);

    if (leftPermanentSpotlight !== rightPermanentSpotlight) {
      return rightPermanentSpotlight ? 1 : -1;
    }

    if (
      leftEntitlements.higherFeaturedRotationPriority !==
      rightEntitlements.higherFeaturedRotationPriority
    ) {
      return rightEntitlements.higherFeaturedRotationPriority ? 1 : -1;
    }

    if (leftBoost !== rightBoost) {
      return rightBoost - leftBoost;
    }

    if (leftFeaturedBoost !== rightFeaturedBoost) {
      return rightFeaturedBoost - leftFeaturedBoost;
    }

    if (leftViews !== rightViews) {
      return rightViews - leftViews;
    }

    return normalizeEditorMainGame(left?.profileInfo?.mainGame).localeCompare(
      normalizeEditorMainGame(right?.profileInfo?.mainGame)
    );
  });
}

function rotatePriorityPool(
  editors = [],
  surfaceKey = "default",
  now = Date.now()
) {
  const seedBase = `${surfaceKey}:${getDayKey(now)}`;
  const stableEditors = sortByStableFeaturedScore(editors, now);
  const priorityEditors = stableEditors.filter((editor) =>
    getUserEntitlements(editor).higherFeaturedRotationPriority
  );
  const standardEditors = stableEditors.filter(
    (editor) => !getUserEntitlements(editor).higherFeaturedRotationPriority
  );

  return [
    ...rotateList(priorityEditors, `${seedBase}:priority`),
    ...rotateList(standardEditors, `${seedBase}:standard`),
  ];
}

export function sortEditorsForSpotlight(
  editors = [],
  surfaceKey = "homepage-spotlight",
  now = Date.now()
) {
  return rotatePriorityPool(
    editors.filter((editor) => isEditorSpotlightActive(editor, now)),
    surfaceKey,
    now
  );
}

export function sortEditorsForFeaturedSurface(
  editors = [],
  surfaceKey = "featured-surface",
  now = Date.now()
) {
  const featuredEditors = rotatePriorityPool(
    editors.filter((editor) => isEditorFeaturedActive(editor, now)),
    `${surfaceKey}:featured`,
    now
  );
  const nonFeaturedEditors = [...editors]
    .filter((editor) => !isEditorFeaturedActive(editor, now))
    .sort((left, right) =>
      String(left?.profileInfo?.username || left?.id || "").localeCompare(
        String(right?.profileInfo?.username || right?.id || "")
      )
    );

  return [...featuredEditors, ...nonFeaturedEditors];
}

export function getHomepageSpotlightEditors(
  editors = [],
  count = 3,
  now = Date.now()
) {
  return sortEditorsForSpotlight(editors, "homepage-spotlight", now).slice(
    0,
    count
  );
}

export function getGameSpotlightEditors(
  editors = [],
  gameName = "",
  count = 3,
  now = Date.now()
) {
  const normalizedGameName = normalizeEditorMainGame(gameName).toLowerCase();

  return sortEditorsForSpotlight(
    editors.filter((editor) => {
      const editorGame = normalizeEditorMainGame(
        editor?.profileInfo?.mainGame
      ).toLowerCase();

      return (
        isEditorSpotlightActive(editor, now) &&
        editorGame &&
        editorGame === normalizedGameName
      );
    }),
    `game-spotlight:${normalizedGameName || "all"}`,
    now
  ).slice(0, count);
}
